import { ICdnStorageConfig } from "@gtmhub/env";
import { IInsightboardItem } from "./models/models";

/**
 * After update of ui-bootstrap, some insights might try to use the old syntax
 * for some of the components, so here we will ensure they use the new syntax.
 */
const ensureCorrectUiBootstrapComponents = (view: string): string => {
  if (!view) {
    return view;
  }

  view = view.replace(/(<\/?)tabset(\s|>)/g, "$1uib-tabset$2");
  view = view.replace(/(<\/?)tab(\s|>)/g, "$1uib-tab$2");
  view = view.replace(/(\s+)(popover|tooltip)=(["'])/g, "$1uib-$2=$3");

  return view;
};

/**
 * When loading jQuery after Angular.js, Angular.js uses its internal JQLite implementation,
 * which is very limited compared to jQuery. One side-effect is that `angular.element` can
 * only work with DOM elements / jQuery selectors rather than any string selector. This fix
 * replace cases of `angular.element(selector)` with `angular.element($(selector))` to
 * support usages in insights.
 */
const ensureJquerySelectors = (view: string): string => {
  if (!view) {
    return view;
  }

  const jqueryMethods = ["show", "hide"];
  for (const method of jqueryMethods) {
    view = view.replace(new RegExp(`angular\\.element\\("([^"]+)"\\)\\.${method}\\(\\)`, "g"), `$("$1").${method}()`);
  }

  view = view.replace(/angular\.element\("([^"]+)"\)/g, 'angular.element($("$1"))');
  view = view.replace(/angular\.element\('([^']+)'\)/g, "angular.element($('$1'))");

  return view;
};

/**
 * We have removed moment.js from the application, but some insights are
 * using the following Angular.js filters: amDate, amTimeAgo, amDifference
 */
const replaceMomentAngularFilters = (view: string): string => {
  if (!view) {
    return view;
  }

  view = view.replace(/amDifference/g, "ghDateDifference");
  view = view.replace(/amTimeAgo/g, "ghTimeAgo");
  view = view.replace(/amDateFormat/g, "ghDate");

  return view;
};

const replaceLegacyCssLinkHref = (view: string): string => {
  if (!view) {
    return view;
  }

  // In process reports we load manually CSS for the heatmap script, but it is loaded from
  // `/cal-heatmap.css`. This function replaces the path with `/dist/assets/libraries/heatmap.css`
  view = view.replace(/"\/cal-heatmap\.css"/g, '"/dist/assets/libraries/heatmap.css"');

  // Some insights refer to CSS within /assets/ folder, but this is not longer supported.
  // The correct folder is /dist/assets/*
  view = view.replace(/"\/(assets\/.+\.css)"/g, '"/dist/$1"');

  return view;
};

const replaceLinkHrefWithCdn = (view: string, cdnStorage?: ICdnStorageConfig): string => {
  if (!view || !cdnStorage || !cdnStorage.enabled) {
    return view;
  }

  view = view.replace(/href="\/([^"]+)"/g, `href="${cdnStorage.url}/$1"`);
  return view;
};

const replaceSrcWithNgSrc = (view: string): string => {
  if (!view) {
    return view;
  }

  view = view.replace(/(\s+)src="([^"]+)"/g, `$1ng-src="$2"`);
  return view;
};

export const ensureCorrectInsightView = (view: string, cdnStorage?: ICdnStorageConfig): string =>
  [ensureCorrectUiBootstrapComponents, ensureJquerySelectors, replaceMomentAngularFilters, replaceLegacyCssLinkHref, replaceLinkHrefWithCdn, replaceSrcWithNgSrc].reduce(
    (view, fn) => fn(view, cdnStorage),
    view
  );

export const sortInsightBoardItems = (array: IInsightboardItem[]): IInsightboardItem[] => {
  return array.sort((a, b) => {
    if (a.row == b.row) {
      return a.col > b.col ? 1 : -1;
    }
    return a.row > b.row ? 1 : -1;
  });
};
